import { faAngleLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useInfiniteQueryEventRsvpPastAsMember,
  useInfiniteQueryEventRsvpUpcomingAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import useGymflowModels from "apps/portal/src/store";
import { useContext , useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { usePortalRoutes } from "../../../hooks";
import { RouteFeature } from "../../../routes";
import { Button, InfiniteScrollContainer, Spinner } from "../../atoms";
import { BookingItem } from "../../atoms/BookingItem/BookingItem";
import { EmptyPage } from "../../atoms/EmptyPage";
import HostedPagesProfileAvatar from "../components/HostedPagesProfileAvatar";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import {
  HostedPagesProfileBookingClassesFilterPopover,
  HostedPagesProfileBookingClassesFilterPopoverProps,
} from "./components/HostedPagesProfileBookingClassesFilterPopover";
import { BOOKING_CLASSES_TABS } from "./constants";
import { BookingClassesTabs } from "./types";

export const HostedPagesProfileBookingClasses = () => {
  const { createSiteOrEmbedLink } = usePortalRoutes();

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const [currentTab, setCurrentTab] = useState<BookingClassesTabs>("upcoming");

  const [filter, setFilter] = useState<
    HostedPagesProfileBookingClassesFilterPopoverProps["filter"]
  >({
    activityCategoryId: [],
    activityId: [],
    eventFacilityId: [],
  });

  const isUpcoming = currentTab === "upcoming";

  const { userId: id } = useContext(AuthenticationContext);
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const { api } = useGymflowModels();
  const history = useHistory();

  const queryEventRsvpUpcoming = useInfiniteQueryEventRsvpUpcomingAsMember({
    api,
    filters: filter,
    enabled: isUpcoming,
  });

  const queryEventRsvpPast = useInfiniteQueryEventRsvpPastAsMember({
    api,
    filters: filter,
    enabled: currentTab === "past",
  });

  const { data: dataBookings, isLoading: isLoadingBookings } = isUpcoming
    ? queryEventRsvpUpcoming
    : queryEventRsvpPast;

  const listOfBookings = dataBookings?.pages.flatMap((e) => e.content) ?? [];

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <div className="track-height bg-gray-0 flex flex-col gap-4 border-b border-gray-300 p-4 lg:px-28 dark:border-gray-700 dark:bg-gray-950">
        <div className="flex flex-row flex-wrap items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-4">
            <Button
              intent="transparent"
              className="min-h-0 min-w-0 p-1"
              onClick={() => history.goBack()}
            >
              <FontAwesomeIcon
                icon={faAngleLeft}
                className="size-5 text-gray-500 dark:text-gray-400"
              />
            </Button>
            <div className="dark:text-gray-25 text-xl font-medium text-gray-950">
              {t("page.hostedPagesProfileBookingClasses.header")}
            </div>
            <div className="hidden flex-row items-center gap-2 lg:flex">
              {BOOKING_CLASSES_TABS.map(({ id, title }) => (
                <Button
                  key={id}
                  size="small"
                  className={cn("text-sm transition-all", {
                    "!border-secondary-500 !text-secondary-500":
                      currentTab === id,
                  })}
                  onClick={() => setCurrentTab(id)}
                >
                  {title}
                </Button>
              ))}
            </div>
          </div>
          <div className="flex flex-row gap-1.5">
            <HostedPagesProfileBookingClassesFilterPopover
              filter={filter}
              onChange={setFilter}
            />
            {isEmbed && id && <HostedPagesProfileAvatar />}
          </div>
        </div>
        <div className="flex flex-row gap-2 lg:hidden">
          {BOOKING_CLASSES_TABS.map(({ id, title }) => (
            <Button
              key={id}
              size="small"
              className={cn("text-sm transition-all flex-1", {
                "!border-secondary-500 !text-secondary-500": currentTab === id,
              })}
              onClick={() => setCurrentTab(id)}
            >
              {title}
            </Button>
          ))}
        </div>
      </div>
      <InfiniteScrollContainer
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-900 py-6 px-4 gap-3",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
        query={isUpcoming ? queryEventRsvpUpcoming : queryEventRsvpPast}
      >
        {listOfBookings?.length > 0 ? (
          listOfBookings.map((item, index) => (
            <BookingItem
              key={item.id}
              as="class"
              type={currentTab}
              info={item}
              previousDateISO={
                index > 0 ? listOfBookings[index - 1].startDate : undefined
              }
            />
          ))
        ) : isLoadingBookings ? (
          <Spinner className="size-7" />
        ) : (
          <div className="flex w-full flex-col items-center justify-center pt-10">
            <EmptyPage
              textOverride={
                isUpcoming
                  ? t(
                      "page.hostedPagesProfileBookingClasses.emptyPage.titleNoUpcoming",
                    )
                  : t(
                      "page.hostedPagesProfileBookingClasses.emptyPage.titleNoPast",
                    )
              }
              description={
                isUpcoming
                  ? t(
                      "page.hostedPagesProfileBookingClasses.emptyPage.description",
                    )
                  : undefined
              }
              action={
                isUpcoming ? (
                  <Button
                    intent="secondary"
                    link={createSiteOrEmbedLink(
                      RouteFeature.SiteTimetableType.replace(
                        ":viewType",
                        "calendar",
                      ),
                    )}
                  >
                    {t(
                      "page.hostedPagesProfileBookingClasses.emptyPage.action",
                    )}
                  </Button>
                ) : undefined
              }
            />
          </div>
        )}
      </InfiniteScrollContainer>
    </div>
  );
};
